import { GetBookingBusinessDocument } from '../graphql/queries/__generated__/GetBookingBusiness.interface';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { GetBookingBusinessResponse } from 'owa-graph-schema';
import { hasError } from '../utils/apolloErrorHandler';
import bookingApolloQuery from '../utils/bookingApolloQuery';
import type GetBookingBusinessResult from '../constants/getBookingBusinessResult';
import { isApolloError } from '@apollo/client';
import { checkGraphQLErrorStatusCode } from '../utils/checkGraphQLErrorStatusCode';
import { PERMISSION_DENIED } from '../constants/errorConstants';
import fetchSourceHeaders from '../utils/fetchSourceHeaders';
import type FeatureSource from '../constants/source';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getToken } from '../utils/callMsGraph';

const ALLOW_LIST = 'AllowList';
const BLOCK_LIST = 'BlockList';

const getBookingBusiness = async (
    businessId: string,
    source?: string
): Promise<GetBookingBusinessResult> => {
    const perfDatapoint = new PerformanceDatapoint('getBookingBusiness');
    try {
        var headers = fetchSourceHeaders(source);
        const response = await bookingApolloQuery(
            GetBookingBusinessDocument,
            {
                variables: { businessId },
                context:
                    // If the feature is enabled and the source is BookWithMe, adding graph token to the headers for B2 to use
                    // TODO: Remove this logic once Preauth is completed for Bookings B2 Appid
                    isFeatureEnabled('bookings-additionalAuthToken') && source === 'BookWithMe'
                        ? {
                              headers: {
                                  ...headers?.headers,
                                  'X-Ms-Migration': (await getToken()) as string,
                              },
                          }
                        : headers,
            },
            { mailboxId: businessId, source /** mailboxId */ }
        );

        //This hasError check is added to evenly log telemetry in orchestrator
        if (!hasError(response)) {
            perfDatapoint.addCustomData({
                entryPoint: source ?? 'booking',
            });
            perfDatapoint.end();
            return response.data?.getBookingBusiness as GetBookingBusinessResponse;
        } else {
            perfDatapoint.endWithError(DatapointStatus.ServerError);
            return { ...response, hasError: true };
        }
    } catch (e) {
        perfDatapoint.endWithError(DatapointStatus.ServerError, e);
        if (isApolloError(e) && checkGraphQLErrorStatusCode(e, PERMISSION_DENIED)) {
            if (
                isFeatureEnabled('bookwithme-accessCheckFromB2') &&
                source === 'BookWithMe' &&
                (checkGraphQLErrorStatusCode(e, ALLOW_LIST) ||
                    checkGraphQLErrorStatusCode(e, BLOCK_LIST))
            ) {
                throw new Error('ErrorDisabledByAdmin');
            }
            throw new Error('PermissionDenied');
        }
        return Promise.reject(new Error('Error in getBookingBusiness'));
    }
};

export default getBookingBusiness;
